import { useState, useEffect, useRef, forwardRef } from 'react'
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'

interface Option {
  id: number
  name: string
}

interface AsyncSelectProps {
  value: number
  onChange: (value: number) => void
  onSearch: (query: string) => Promise<Option[]>
  placeholder: string
  label: string
  error?: string
  required?: boolean
  disabled?: boolean
  searchQuery?: string
  setSearchQuery?: (q: string) => void
  debouncedSearchQuery?: string
}

// Componente de esqueleto para las opciones
const OptionSkeleton = () => (
  <div className="w-full px-3 py-2 animate-pulse">
    <div className="flex items-center space-x-3">
      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      <div className="h-3 bg-gray-100 rounded w-1/4"></div>
    </div>
  </div>
)

// Componente de esqueleto para el estado de carga inicial
const InitialLoadingSkeleton = () => (
  <div className="p-2 space-y-2">
    {Array.from({ length: 5 }).map((_, index) => (
      <div key={index} className="flex items-center space-x-3 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        <div className="h-3 bg-gray-100 rounded w-1/3"></div>
      </div>
    ))}
  </div>
)

// Componente de esqueleto para búsqueda
const SearchLoadingSkeleton = () => (
  <div className="p-2 space-y-2">
    {Array.from({ length: 3 }).map((_, index) => (
      <div key={index} className="flex items-center space-x-3 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        <div className="h-3 bg-gray-100 rounded w-1/4"></div>
      </div>
    ))}
  </div>
)

// Cache simple para almacenar resultados de búsqueda
const searchCache = new Map<string, { data: Option[], timestamp: number }>()
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutos

// NUEVO: función para filtrar localmente
const localFilter = (options: Option[], query: string) => {
  if (!query) return options
  return options.filter(opt => opt.name.toLowerCase().includes(query.toLowerCase()))
}

// Generar un ID único para cada instancia del componente
let instanceCounter = 0
const getInstanceId = () => {
  instanceCounter++
  return `async-select-${instanceCounter}`
}

// Cache global para datos iniciales (sin búsqueda)
const initialDataCache = new Map<string, { data: Option[], timestamp: number }>()

const AsyncSelect = forwardRef<HTMLDivElement, AsyncSelectProps>(
  ({
    value,
    onChange,
    onSearch,
    placeholder,
    label,
    error,
    required = false,
    disabled = false,
    searchQuery: controlledSearchQuery,
    setSearchQuery: setControlledSearchQuery,
    debouncedSearchQuery,
    ...props
  }, ref) => {
  const [isOpen, setIsOpen] = useState(false)
  const [internalSearchQuery, setInternalSearchQuery] = useState('')
  const searchQuery = controlledSearchQuery !== undefined ? controlledSearchQuery : internalSearchQuery;
  const setSearchQuery = setControlledSearchQuery !== undefined ? setControlledSearchQuery : setInternalSearchQuery;
  const [options, setOptions] = useState<Option[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedOption, setSelectedOption] = useState<Option | null>(null)
  const [initialLoading, setInitialLoading] = useState(false)
  const [isFromCache, setIsFromCache] = useState(false)
  
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const instanceId = useRef(getInstanceId())

  // Función para obtener datos del cache o hacer la búsqueda
  const getCachedOrSearch = async (query: string): Promise<Option[]> => {
    const cacheKey = `${instanceId.current}-${query.toLowerCase()}`
    const cached = searchCache.get(cacheKey)
    
    // Verificar si hay datos en cache y no han expirado
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setIsFromCache(true)
      // Simular un pequeño delay para mostrar que viene del cache
      await new Promise(resolve => setTimeout(resolve, 100))
      return cached.data
    }
    
    // NUEVO: Si hay cache de búsqueda vacía, filtrar localmente
    const allCacheKey = `${instanceId.current}-`
    const allCached = searchCache.get(allCacheKey)
    if (allCached && Date.now() - allCached.timestamp < CACHE_DURATION && query) {
      setIsFromCache(true)
      const filtered = localFilter(allCached.data, query)
      // No guardamos este resultado filtrado en cache para queries específicas
      return filtered
    }
    
    // Si no hay cache válido, hacer la búsqueda
    setIsFromCache(false)
    const results = await onSearch(query)
    
    // Guardar en cache
    searchCache.set(cacheKey, {
      data: results,
      timestamp: Date.now()
    })
    
    return results
  }

  // Lógica de búsqueda: si se pasa debouncedSearchQuery, usarla, si no, usar searchQuery local con debounce interno
  useEffect(() => {
    const queryToUse = debouncedSearchQuery !== undefined ? debouncedSearchQuery : searchQuery;
    if (queryToUse === '' && options.length > 0) {
      return;
    }
    setLoading(true);
    getCachedOrSearch(queryToUse)
      .then(setOptions)
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, searchQuery, onSearch]);

  // Cargar datos iniciales cuando se abre
  useEffect(() => {
    if (isOpen && searchQuery === '' && options.length === 0) {
      setInitialLoading(true)
      handleSearch('').finally(() => setInitialLoading(false))
    }
  }, [isOpen])

  // Encontrar la opción seleccionada cuando cambia el valor
  useEffect(() => {
    if (value && options.length > 0) {
      const selected = options.find(option => option.id === value)
      setSelectedOption(selected || null)
    } else {
      setSelectedOption(null)
    }
  }, [value, options])

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearch = async (query: string) => {
    // Si es búsqueda vacía y ya tenemos datos, no hacer request
    if (query === '' && options.length > 0) {
      return
    }
    
    setLoading(true)
    try {
      const results = await getCachedOrSearch(query)
      setOptions(results)
    } catch (error) {
      setOptions([])
    } finally {
      setLoading(false)
    }
  }

  const handleOptionSelect = (option: Option) => {
    onChange(option.id)
    setSelectedOption(option)
    setIsOpen(false)
    setSearchQuery('')
  }

  const handleInputFocus = () => {
    setIsOpen(true)
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }

  return (
    <div className="relative" ref={ref || dropdownRef} aria-invalid={!!error}>
      <label className={`block text-sm font-medium mb-1 ${error ? 'text-destructive' : 'text-gray-700'}`}>
        {label}
      </label>
      
      {/* Trigger button */}
      <button
        type="button"
        onClick={handleInputFocus}
        disabled={disabled}
        aria-invalid={!!error}
        className={
          `flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm
          ${error ? 'border-destructive' : 'border-input'}
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-gray-400'}`
        }
      >
        <div className="flex items-center justify-between">
          <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
            {selectedOption ? selectedOption.name : placeholder}
          </span>
        </div>
      </button>

      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar..."
                className="flex h-9 w-full rounded-md border border-input bg-transparent pl-8 pr-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                autoFocus
              />
            </div>
          </div>

          {/* Options list */}
          <div className="max-h-48 overflow-y-auto">
            {initialLoading ? (
              // Esqueletos para carga inicial
              <InitialLoadingSkeleton />
            ) : loading ? (
              // Esqueletos para búsqueda
              <SearchLoadingSkeleton />
            ) : options.length > 0 ? (
              <>
                {options.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleOptionSelect(option)}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 focus:outline-none focus:bg-blue-50 ${
                      option.id === value ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                    }`}
                  >
                    {option.name}
                  </button>
                ))}
              </>
            ) : (
              <div className="p-3 text-center text-sm text-gray-500">
                {searchQuery ? 'No se encontraron resultados' : 'No hay opciones disponibles'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
})
AsyncSelect.displayName = 'AsyncSelect'
export default AsyncSelect
